import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input, Button, Alert, Counter, useIsMobile } from '../components/ui';
import api from '../utils/api';
import LogoLockup from '../assets/HopeForSomeone.png';
import LogoIcon from '../assets/logo-icon.png';

function BrandPanel() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get('/stats/platform').then(r => setStats(r.data)).catch(() => {}); }, []);

  const panelStats = stats ? [
    [<Counter value={stats.total_donors} suffix="+" />, 'Registered Donors'],
    [<Counter value={stats.total_camps} />, 'Donation Camps'],
    [<Counter value={stats.cities_covered} />, 'Cities Covered'],
  ] : null;

  return (
    <div style={{
      width: '44%', flexShrink: 0,
      background: 'linear-gradient(145deg, var(--red-deeper) 0%, var(--red-dark) 45%, var(--red) 100%)',
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      padding: '64px 52px', position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: '-90px', right: '-90px', width: '340px', height: '340px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
      <div style={{ position: 'absolute', bottom: '-100px', left: '-70px', width: '280px', height: '280px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* The lockup's navy/red wordmark disappears on this red panel, so the
            mark goes on a white tile and the name stays as type. */}
        <div style={{ marginBottom: '52px', display: 'flex', alignItems: 'center', gap: '11px' }}>
          <div style={{
            width: '44px', height: '44px', borderRadius: '11px', background: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            boxShadow: '0 4px 14px rgba(0,0,0,0.16)',
          }}>
            <img src={LogoIcon} alt="" style={{ width: '32px', height: '32px', display: 'block' }} />
          </div>
          <div style={{ fontSize: '18px', fontWeight: 800, color: 'white', letterSpacing: '-0.03em' }}>
            Hope<span style={{ opacity: 0.65 }}>ForSomeone</span>
          </div>
        </div>

        <h2 style={{ fontSize: '32px', fontWeight: 900, color: 'white', lineHeight: 1.1, letterSpacing: '-0.04em', marginBottom: '14px' }}>
          Every drop<br />saves a life.
        </h2>
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, marginBottom: '52px' }}>
          Connecting donors, recipients, and organizations in real time — anywhere.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {panelStats ? panelStats.map(([n, l]) => (
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div style={{ fontSize: '22px', fontWeight: 900, color: 'white', minWidth: '80px', letterSpacing: '-0.04em' }}>{n}</div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>{l}</div>
            </div>
          )) : [0, 1, 2].map(i => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '20px', animation: 'bb-pulse 1.6s ease-in-out infinite' }}>
              <div style={{ width: '60px', height: '22px', borderRadius: '5px', background: 'rgba(255,255,255,0.15)' }} />
              <div style={{ width: '100px', height: '13px', borderRadius: '4px', background: 'rgba(255,255,255,0.1)' }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');

  const set = k => e => {
    setFieldErrors(f => ({ ...f, [k]: undefined }));
    setForm(f => ({ ...f, [k]: e.target.value }));
  };

  const validate = () => {
    const errs = {};
    if (!form.email.includes('@')) errs.email = 'Enter a valid email address.';
    if (!form.password) errs.password = 'Enter your password.';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true); setError('');
    try {
      const user = await login(form.email, form.password);
      navigate(redirect || (user.role === 'donor' ? '/donor/profile' : user.role === 'organization' ? '/org/dashboard' : '/'));
    } catch {
      setError('Incorrect email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bb-auth-shell">
      {!isMobile && <BrandPanel />}

      {/* Form panel */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--white)',
        padding: isMobile ? '24px 18px 32px' : '64px 60px',
      }}>
        <div style={{ width: '100%', maxWidth: '380px', animation: 'bb-fadeUp 0.5s ease both' }}>
          {isMobile && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0px' }}>
              <img src={LogoLockup} alt="HopeForSomeone" style={{ height: '200px', width: 'auto', display: 'block' }} />
            </div>
          )}

          <div style={{ marginBottom: '28px', textAlign: isMobile ? 'center' : 'left' }}>
            <h1 style={{ fontSize: isMobile ? '23px' : '26px', fontWeight: 800, letterSpacing: '-0.03em', marginBottom: '6px', color: 'var(--gray-900)' }}>
              Welcome back
            </h1>
            <p style={{ fontSize: '14px', color: 'var(--gray-500)' }}>
              Sign in to your HopeForSomeone account
            </p>
          </div>

          <Input
            label="Email"
            type="email"
            autoFocus
            placeholder="you@email.com"
            value={form.email}
            onChange={set('email')}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            error={fieldErrors.email}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <label style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--gray-700)' }}>Password</label>
            <Link to="/forgot-password" style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--red)' }}>
              Forgot password?
            </Link>
          </div>
          <Input
            type="password"
            placeholder="••••••••"
            value={form.password}
            onChange={set('password')}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            error={fieldErrors.password}
          />

          {error && <Alert variant="error">{error}</Alert>}

          <Button variant="primary" size="lg" fullWidth loading={loading} onClick={handleSubmit}>
            Sign In
          </Button>

          <p style={{ textAlign: 'center', fontSize: '13.5px', color: 'var(--gray-500)', marginTop: '20px' }}>
            New to HopeForSomeone?{' '}
            <Link
              to={`/register${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ''}`}
              style={{ fontWeight: 600, color: 'var(--red)' }}
            >
              Create account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
